import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { envConfig, GameId, partnerSettingsStore, sessionStore } from '@bg-shared';
import { DEFAULT_PRESET_LOBBY_TABS_ICONS } from '../constants';
import { LobbyTabId, LobbyCategories } from '../enums';
import { ILobbyTab } from '../interfaces';

interface IUseLobbyTabsResponse {
    activeTabId: LobbyTabId;
    setActiveTabId: (id: LobbyTabId) => void;
    getGamesByTab: (tabId: LobbyTabId) => GameId[];
    availableTabs: ILobbyTab[];
    hwbGames: GameId[];
}

export const useLobbyTabsHook = (): IUseLobbyTabsResponse => {
    const lobbyTabs = useMemo(() => {
        let defaultTabs = [
            {
                id: LobbyTabId.ALL_CATEGORIES,
                title: LobbyCategories.ALL_CATEGORIES,
                icon: DEFAULT_PRESET_LOBBY_TABS_ICONS[LobbyCategories.ALL_CATEGORIES],
            },
            ...Object.entries(partnerSettingsStore.gamesCategories)
                .filter(([, games]) => !!games.length)
                .map(([category], index) => ({
                    id: index + 1,
                    title: category,
                    icon: DEFAULT_PRESET_LOBBY_TABS_ICONS[category as LobbyCategories] || null,
                })),
        ];

        if (
            (envConfig.isDevEnv || envConfig.isStagingEnv) &&
            partnerSettingsStore.gamesCategories[LobbyCategories.SLOTS] &&
            !partnerSettingsStore.gamesCategories[LobbyCategories.SLOTS].includes(
                GameId.LUCKY_LENNY,
            )
        ) {
            defaultTabs.push({
                id: defaultTabs.length,
                title: LobbyCategories.SLOTS,
                icon: DEFAULT_PRESET_LOBBY_TABS_ICONS[LobbyCategories.SLOTS],
            });
        }

        // HWB partner launcher without custom category
        if (_featuresConfig.hwbSeparateCategory && !sessionStore.lobbyLiveGames) {
            defaultTabs = defaultTabs.filter((tab) => tab.title !== LobbyCategories.CUSTOM1);
        }

        return defaultTabs;
    }, [partnerSettingsStore.gamesCategories]);

    const [activeTabId, setActiveTabId] = useState<LobbyTabId>(
        lobbyTabs[sessionStore.lobbyDefaultTab]?.id || lobbyTabs[0].id,
    );

    const { gamesPerTab, availableTabs, hwbGames } = useMemo(() => {
        const brandedTabs = lobbyTabs.map((tab) => {
            let iconComponent: FunctionComponent;

            if (
                [
                    LobbyCategories.CUSTOM1,
                    LobbyCategories.CUSTOM2,
                    LobbyCategories.CUSTOM3,
                    LobbyCategories.CUSTOM4,
                    LobbyCategories.CUSTOM5,
                ].includes(tab.title as LobbyCategories)
            ) {
                const customCategoryIcon = _featuresConfig?.brandedConfig?.lobbyIcons?.[
                    tab.title
                ] as { default: string; branded?: string };

                if (customCategoryIcon) {
                    iconComponent = () => (
                        <img
                            src={customCategoryIcon?.branded || customCategoryIcon.default}
                            alt="tab-icon"
                        />
                    );
                }
            }

            return {
                ...tab,
                icon: iconComponent || tab.icon,
            };
        });

        // HWB partner launcher with only 1 category
        if (_featuresConfig.hwbSeparateCategory && sessionStore.lobbyLiveGames) {
            return {
                hwbGames: partnerSettingsStore.gamesCategories[LobbyCategories.CUSTOM1],
            };
        }

        const gamesList = Object.fromEntries(
            brandedTabs.map((tab) => [
                tab.id,
                partnerSettingsStore.gamesCategories[tab.title] || [],
            ]),
        );

        return { gamesPerTab: gamesList, availableTabs: brandedTabs };
    }, [partnerSettingsStore.gamesCategories]);

    const getGamesByTab = useCallback(
        (tabId: LobbyTabId): GameId[] => {
            if (tabId === LobbyTabId.ALL_CATEGORIES) {
                return Object.values(gamesPerTab).flat();
            }
            return gamesPerTab[tabId] ?? [];
        },
        [gamesPerTab],
    );

    return {
        hwbGames,
        activeTabId,
        getGamesByTab,
        setActiveTabId,
        availableTabs,
    };
};
